import { memo } from 'react';
import { Card, CardMedia, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useObjectUrl } from 'hooks/useObjectUrl';

type ImageUrlPreviewProps = {
  url: string;
  onDelete: () => void;
};

type ImageDataPreviewProps = {
  data: File | Blob;
  onDelete: () => void;
};

const useStyles = makeStyles()((theme) => ({
  imagePreview: {
    minHeight: 400,
    width: '100%',
    borderRadius: Number(theme.shape.borderRadius) * 2,
    backgroundSize: 'contain',
    backgroundColor: theme.palette.grey[300],
  },
  deleteButton: {
    backgroundColor: theme.palette.common.black,
    // Do not rely on `IconButton color="primary"`: in the new light palette `primary.main`
    // matches `common.black`, so the glyph would be invisible on this chip.
    color: theme.palette.common.white,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    height: 42,
    width: 42,
    '&:hover': {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    '& .MuiSvgIcon-root': {
      color: 'inherit',
    },
    position: 'absolute',
    top: 16,
    right: 16,
  },
}));

export const ImageUrlPreview = memo(
  (props: ImageUrlPreviewProps): JSX.Element => {
    const { url, onDelete } = props;
    const { classes } = useStyles();
    const { t } = useTranslation();

    return (
      <Card sx={{ position: 'relative' }}>
        <CardMedia className={classes.imagePreview} image={url} />
        <IconButton
          size="medium"
          aria-label={t('imagePreview.deleteImage')}
          className={classes.deleteButton}
          onClick={onDelete}
        >
          <CloseIcon />
        </IconButton>
      </Card>
    );
  },
);

ImageUrlPreview.displayName = 'ImageUrlPreview';

export const ImageDataPreview = memo(
  (props: ImageDataPreviewProps): JSX.Element => {
    const { data, onDelete } = props;
    const { classes } = useStyles();
    const { t } = useTranslation();

    const objectUrl = useObjectUrl(data);

    return (
      <Card sx={{ position: 'relative' }}>
        <CardMedia
          className={classes.imagePreview}
          image={objectUrl}
          // @ts-expect-error - name is a valid property
          title={data.name}
        />
        <IconButton
          size="medium"
          aria-label={t('imagePreview.deleteImage')}
          className={classes.deleteButton}
          onClick={onDelete}
        >
          <CloseIcon />
        </IconButton>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.data === nextProps.data;
  },
);

ImageDataPreview.displayName = 'ImageDataPreview';
